import {
  IFlow,
  ControlName,
  IEventData,
  IEventDataAction,
  IEmailDataAction,
  IEmailData,
  ControlNameEnum,
  ConditionEmails,
  ControlTypeEnum,
  IShareProfileTemplate,
} from './interfaces';
import nextId from 'react-id-generator';
import { v4 as uuidv4 } from 'uuid';
import store from 'store';
import { convertUnicodeInStringToEmojis } from 'utils';

export const findParentNode = (nodeId: string, workflow: IFlow): string => {
  let workflowKeys = Object.keys(workflow);
  for (let key of workflowKeys) {
    if (workflow[key]?.childNodes?.includes(nodeId)) {
      return key;
    }
  }
  return '';
};

export const basedOnValues = {
  allDays: { value: 'all_days', label: 'All Days (Mon - Sun)' },
  businessDays: { value: 'business_days', label: 'Business Days (Mon - Fri)' },
};

export const findDropAreaData = (type: ControlName) => {
  let { workflow, controls } = store.getState().automation;
  const { dropArea } = workflow;
  let obj = controls.find((control) => control.name === type);
  return {
    ...dropArea,
    title: obj?.title,
    controlName: obj?.name,
    controlType: obj?.type,
    controlId: obj?.id,
  };
};

export const serializeEventsData = (events: IEventDataAction[]) => {
  let data: IEventData[] = [];

  events.map((event: IEventDataAction) => {
    data.push({
      id: event.id,
      title: event.title,
      type: event.type,
      info: event.description,
      iconType: event.name.split(' ')[1] ?? event.name, // if event name is one word then event name is icon type
    });
  });

  return data;
};

export const serializeEmailsData = (emails: IEmailDataAction[]) => {
  let data: IEmailData[] = [];

  emails.map((email: IEmailDataAction) => {
    data.push({
      campaignId: email.campaign_id,
      campaignName: email.campaign_name,
      emailId: email.email_id,
      versionEmailType: email.email_type,
      designReady: email.design_ready,
      title: email.email_name,
      subject: convertUnicodeInStringToEmojis(email.subject),
      sendFrom: email.sent_from,
      senderName: email.sender_name,
      date: email.created_on,
      thumbnail: email.thumbnail[0],
      platform: email.email_platform,
    });
  });
  return data;
};

export const getConditionEmails = (id: string, shareProfileTemplates: IShareProfileTemplate[]): ConditionEmails[] => {
  const {
    flow: workFlow,
    detail: { emailId, emailName: title, shareProfileUuid },
  } = store.getState().automation.workflow;

  const shareProfileTemplateIds: number[] = shareProfileTemplates.map((template) => template.id);

  let nodeId = id;
  let emails: ConditionEmails[] = [];
  const uniqueIds = new Set();
  while (nodeId) {
    if (workFlow[nodeId]?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
      let data = workFlow[nodeId]?.detail;
      if (data?.sp_uuid) {
        emails.push({
          emailId: data.emailId,
          title: data.emailName,
          sp_uuid: data.sp_uuid,
        });
        uniqueIds.add(data.emailId);
      } else if (!uniqueIds.has(data.emailId)) {
        emails.push({
          emailId: data.emailId,
          title: data.emailName,
          ...(shareProfileTemplateIds.includes(data.emailId) ? { sp_uuid: uuidv4() } : {}),
        });
        uniqueIds.add(data.emailId);
      }
    }
    nodeId = findParentNode(nodeId, workFlow);
  }
  let nodeTitle = shareProfileUuid ? 'Share Profile' : title;
  emails.unshift({
    emailId,
    title: nodeTitle,
    ...(shareProfileUuid ? { sp_uuid: shareProfileUuid } : {}),
  });
  return emails;
};

export const getConditionNodes = (id: string): any => {
  const { flow: workFlow } = store.getState().automation.workflow;

  let nodeId = id;
  let nodes: any = [];
  const uniqueIds = new Set();
  while (nodeId) {
    if (workFlow[nodeId]?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
      let data = workFlow[nodeId]?.detail;
      if (data?.sp_uuid) {
        nodes.push({
          nodeId: nodeId,
        });
        uniqueIds.add(data.emailId);
      } else if (!uniqueIds.has(data.emailId)) {
        nodes.push({
          nodeId: nodeId,
        });
        uniqueIds.add(data.emailId);
      }
    }
    nodeId = findParentNode(nodeId, workFlow);
  }
  return nodes;
};

export const getSendEmails = (emails: IEmailData[], ignoreNodeId: string | undefined): IEmailData[] => {
  const workflow = store.getState().automation.workflow.flow;
  let workflowKeys = Object.keys(workflow);
  let ids: number[] = [];
  workflowKeys.forEach((item) => {
    if (item !== ignoreNodeId && workflow[item]?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
      ids.push(workflow[item]?.detail?.emailId);
    }
  });
  return emails.filter((email) => !ids.includes(email.emailId));
};

// function to find out that nodeId in which email (send email node) is being used in any if/else condition or not.
export const findEmailUsage = (nodeId: string, emailId: number, spuuid?: string): any => {
  const workFlow = store.getState().automation.workflow.flow;
  let values: string[] = [];
  searchNode(workFlow[nodeId]?.childNodes[0]);
  function searchNode(nodeId: any) {
    if (!nodeId) return;
    else {
      let node = workFlow[nodeId];
      // when added multiple conditions we have to handle it for conditons inside the details
      if (
        node.detail.controlType === ControlTypeEnum.CONDITION ||
        (node?.detail?.controlName === ControlNameEnum.WAIT && node?.detail?.radioValue === 2)
      ) {
        let errorFound = false;
        node.detail.conditions?.forEach((item: any) => {
          if (emailId.toString() === item?.emailId?.toString() || item?.sp_uuid === spuuid) {
            errorFound = true;
          }
        });
        if (errorFound) {
          values.push(nodeId);
        }
      }
      node?.childNodes[0] && searchNode(node?.childNodes[0]);
      node?.childNodes[1] && searchNode(node?.childNodes[1]);
    }
  }
  return values;
};

export const isChild = (nodeId: string, searchId: string) => {
  const workFlow = store.getState().automation.workflow.flow;
  let node = checkChild(nodeId, searchId, workFlow);
  return node;
};

const checkChild = (nodeId: any, searchId: any, workFlow: IFlow): any => {
  let flow = workFlow[nodeId];
  if (flow?.childNodes?.[0] === searchId || flow?.childNodes?.[1] === searchId) {
    return true;
  } else {
    let leftValue = flow?.childNodes[0] && checkChild(flow?.childNodes[0], searchId, workFlow);
    let rightValue = flow?.childNodes[1] && checkChild(flow?.childNodes[1], searchId, workFlow);
    return leftValue || rightValue;
  }
};

export const generateNodeId = (alreadyGeneratedNumber: number): string => {
  let num = parseInt(nextId().replace('id', '')) + alreadyGeneratedNumber;
  return '' + num;
};

export const hasEndControl = (workflow: IFlow): boolean => {
  let workflowKeys = Object.keys(workflow);
  let index = workflowKeys.find((key) => workflow[key]?.detail?.controlName === ControlNameEnum.END);
  return index === undefined ? false : true;
};

export const hasAnyErrorNode = (workflow: IFlow): boolean => {
  let workflowKeys = Object.keys(workflow);
  let index = workflowKeys.find((key) => workflow[key]?.detail?.error);
  return index === undefined ? false : true;
};

export const hasAnyEmptyListNode = (workflow: IFlow): boolean => {
  let anyEmptyListNode: boolean = false;
  searchNode('0');
  function searchNode(nodeId: any) {
    if (!nodeId) return;
    else {
      let node = workflow[nodeId];
      if (node?.detail?.controlName === ControlNameEnum.ADD_LIST && node?.detail?.list?.length === 0) {
        anyEmptyListNode = true;
      }
      node?.childNodes[0] && searchNode(node?.childNodes[0]);
      node?.childNodes[1] && searchNode(node?.childNodes[1]);
    }
  }
  return anyEmptyListNode;
};

export const filtersList = ['allTypes', 'adhoc', 'digest'];

export function onlyUnique(value: any, index: number, self: any) {
  return self.indexOf(value) === index;
}

export const populateEmailLinks = (data: any): any => {
  let emailsObject = data;
  const defaultTitles: any = {
    articles: 'Buttons',
    buttons: 'Buttons',
    texts: 'Texts',
    img_videos: 'Images & Videos',
    social_media: 'Social Media',
  };
  let resultObject: any = {}; // { emailId : {  } }
  Object.keys(emailsObject).map((key: any) => {
    let email = emailsObject[key];
    let linksArray: any = [];
    Object.entries(email).map((linksObj: any) => {
      let object: any = {};
      let objKey = linksObj[0];
      object.type = objKey;
      object.label = defaultTitles[objKey];
      let uniqueLinksArray: any = [];
      let linksOptions: any = [];
      linksObj[1].forEach((element: any) => {
        let linkSingleObject = {
          type: objKey,
          label: element.title !== '' ? element.title : element.link,
          value: element.link,
        };
        let linkIndex = uniqueLinksArray.indexOf(element.link);
        if (linkIndex >= 0) return;
        uniqueLinksArray.push(element.link);
        linksOptions.push(linkSingleObject);
      });
      object.options = linksOptions;
      linksArray.push(object);
    });
    let buttonsLink: any = [];
    let linkArrayMerged = [];
    linksArray.forEach((element: any) => {
      if (element.type.includes('articles') || element.type.includes('buttons')) {
        buttonsLink = [...buttonsLink, ...element.options];
      } else {
        linkArrayMerged.push(element);
      }
    });
    linkArrayMerged.unshift({ options: buttonsLink, label: 'Buttons', type: 'buttons' });

    resultObject[key] = linkArrayMerged;
  });
  return resultObject;
};

export const findUsageOfTriggerEmail = (emailId: number): any => {
  const workFlow = store.getState().automation.workflow.flow;
  let values: string[] = [];
  searchNode('0');
  function searchNode(nodeId: any) {
    if (!nodeId) return;
    else {
      let node = workFlow[nodeId];
      // when added multiple conditions we have to handle it for conditons inside the details
      if (
        node?.detail.controlType === ControlTypeEnum.CONDITION ||
        (node?.detail?.controlName === ControlNameEnum.WAIT && node?.detail?.radioValue === 2)
      ) {
        let errorFound = false;
        node?.detail.conditions?.forEach((item: any) => {
          if (emailId.toString() === item?.emailId?.toString()) {
            errorFound = true;
          }
        });
        if (errorFound) {
          values.push(nodeId);
        }
      }
      node?.childNodes[0] && searchNode(node?.childNodes[0]);
      node?.childNodes[1] && searchNode(node?.childNodes[1]);
    }
  }
  return values;
};

export const findEmailUsageInWholeWorkflow = (emailId: number) => {
  const workFlow = store.getState().automation.workflow.flow;
  let values: string[] = [];
  searchNode('0');
  function searchNode(nodeId: any) {
    if (!nodeId) return;
    else {
      let node = workFlow[nodeId];
      // when added multiple conditions we have to handle it for conditons inside the details
      if (node?.detail?.controlName === ControlNameEnum.SEND_EMAIL && node?.detail?.emailId === emailId) {
        values.push(nodeId);
      } else if (
        node?.detail.controlType === ControlTypeEnum.CONDITION ||
        (node?.detail?.controlName === ControlNameEnum.WAIT && node?.detail?.radioValue === 2)
      ) {
        let errorFound = false;
        node?.detail.conditions?.forEach((item: any) => {
          if (emailId.toString() === item?.emailId?.toString()) {
            errorFound = true;
          }
        });
        if (errorFound) {
          values.push(nodeId);
        }
      }
      node?.childNodes[0] && searchNode(node?.childNodes[0]);
      node?.childNodes[1] && searchNode(node?.childNodes[1]);
    }
  }
  return values;
};

export const findEmailsWithDesignNotReady = () => {
  const workFlow = store.getState().automation.workflow.flow;
  let values: string[] = [];
  searchNode('0');
  function searchNode(nodeId: any) {
    if (!nodeId) return;
    else {
      let node = workFlow[nodeId];
      if (
        node?.detail?.controlName === ControlNameEnum.SEND_EMAIL &&
        !node?.detail?.sp_uuid &&
        node?.detail?.emailId &&
        !node?.detail?.designReady
      )
        values.push(nodeId);
      node?.childNodes[0] && searchNode(node?.childNodes[0]);
      node?.childNodes[1] && searchNode(node?.childNodes[1]);
    }
  }
  return values;
};
